/**
 * scoreDelta.js — iter761 (Feb 2026)
 *
 * "BHI Impact Score Delta" tooltip helper.
 * Captures a compact "score fingerprint" (twin composite + all sub-scores
 * incl. BHI composite) BEFORE and AFTER a condition is added / removed,
 * then formats a Bulgarian toast so the user immediately sees the impact:
 *
 *   BHI: 74 → 68 (−6)
 *   Twin композит: 82 → 79 (−3)
 *   Най-засегнат: възпалителен близнак −5
 *
 * Uses ONE endpoint call each side: /health-twin/twin-score?force_refresh=true
 * (already returns sub_scores.bhi + inflammation/hormonal/metabolic_insulin
 * with cross-influence adjustments baked in).
 */
import axios from "axios";
import { toast } from "sonner";

const TWIN_LABEL_BG = {
  bhi:              "BHI (метаболитна среда)",
  inflammation:     "възпалителен близнак",
  hormonal:         "хормонален близнак",
  metabolic_insulin:"метаболитно-инсулинов близнак",
  thyroid:          "щитовиден близнак",
  cardio:           "сърдечно-съдов близнак",
  liver_kidney:     "чернодробно-бъбречен близнак",
  nutritional:      "нутриционен близнак",
  male_health:      "мъжки здравен близнак",
  cognitive:        "когнитивен близнак",
  sleep:            "сънен близнак",
  stress:           "стресов близнак",
  body_comp:        "телесен състав",
  movement:         "движение",
  mental_emotional: "ментално-емоционален",
  dental:           "дентален",
  microbiome:       "микробиом",
  phenotype:        "фенотип",
  lab:              "лаб. близнак",
};

/**
 * Captures a compact score snapshot. Best-effort — returns null on error.
 * Always uses force_refresh=true so we bypass the 5-min cache.
 */
export async function captureScoreFingerprint(API) {
  try {
    const { data } = await axios.get(
      `${API}/health-twin/twin-score?days=30&force_refresh=true`,
      { timeout: 20000 }
    );
    return {
      twin_composite: Number(data?.composite ?? 0),
      bhi:            data?.sub_scores?.bhi ?? null,
      sub_scores:     data?.sub_scores || {},
    };
  } catch {
    return null;
  }
}

/**
 * Computes { bhi_delta, twin_delta, biggest_change } and returns a
 * formatted Bulgarian message ready for a sonner toast.
 * `kind`: "add" | "remove" — controls the arrow direction & tone.
 */
export function formatDelta(before, after, kind = "add") {
  if (!before || !after) return null;

  const bhi_before = before.bhi;
  const bhi_after  = after.bhi;
  const bhi_delta  = (bhi_before != null && bhi_after != null) ? (bhi_after - bhi_before) : null;

  const tw_before = before.twin_composite;
  const tw_after  = after.twin_composite;
  const tw_delta  = (tw_before != null && tw_after != null) ? (tw_after - tw_before) : 0;

  // Biggest per-twin change (excluding bhi which we report separately)
  let biggest = null;
  for (const key of Object.keys(after.sub_scores || {})) {
    if (key === "bhi") continue;
    const a = after.sub_scores[key];
    const b = before.sub_scores?.[key];
    if (a == null || b == null) continue;
    const d = a - b;
    if (d === 0) continue;
    if (!biggest || Math.abs(d) > Math.abs(biggest.delta)) {
      biggest = { twin: key, before: b, after: a, delta: d };
    }
  }

  const arrow = (d) => (d === 0 ? "±0" : d > 0 ? `+${d}` : `−${Math.abs(d)}`);
  const fmtLine = (label, b, a) =>
    (b != null && a != null && b !== a) ? `${label}: ${b} → ${a} (${arrow(a - b)})` : null;

  const lines = [];
  const bhiLine = fmtLine("BHI", bhi_before, bhi_after);
  if (bhiLine) lines.push(bhiLine);
  const twinLine = fmtLine("Twin композит", tw_before, tw_after);
  if (twinLine) lines.push(twinLine);
  if (biggest) {
    lines.push(`Най-засегнат: ${TWIN_LABEL_BG[biggest.twin] || biggest.twin} (${arrow(biggest.delta)})`);
  }

  // Neutral case — nothing changed (structured condition duplicate, or all
  // affected sub-scores were null / insufficient_data).
  if (lines.length === 0) {
    return { unchanged: true, message: "Диагнозата е записана. BHI и Twin-ите ще я отчитат при следващо обновяване на данните." };
  }

  return {
    unchanged: false,
    kind,
    bhi_delta,
    twin_delta: tw_delta,
    biggest,
    message: lines.join(" · "),
  };
}

/**
 * Convenience: capture before, run action, capture after, toast.
 * `action` is an async fn (e.g. axios.post(...)).
 * Returns the delta object (or null).
 */
export async function withScoreDelta(API, action, { kind = "add" } = {}) {
  const before = await captureScoreFingerprint(API);
  let actionResult;
  try {
    actionResult = await action();
  } catch (err) {
    throw err; // preserve original behavior
  }
  // Give backend a beat to invalidate cache before refetching.
  await new Promise((r) => setTimeout(r, 400));
  const after = await captureScoreFingerprint(API);
  const delta = formatDelta(before, after, kind);
  if (delta) {
    const icon = kind === "remove" ? "↩️" : "🎯";
    if (delta.unchanged) {
      toast.info(delta.message);
    } else {
      // Sonner richColors: use "info" (neutral) — delta sign already communicates direction.
      toast.message(`${icon} Реален ефект върху вашия дигитален двойник`, {
        description: delta.message,
        duration:    7000,
      });
    }
  }
  return { delta, actionResult };
}
